/**
 * Every word the threshold and ICP cards say — Story 20.4.
 *
 * DRAWN is `design/screens/editor-qualify.html`'s own copy, held there verbatim
 * by `tests/escalation-copy.test.ts`. The consequence sentence is drawn as a
 * TEMPLATE: the mockup shows it at 70% for a funnel whose top band is Hot, and the
 * test holds `consequence(70, 'Hot')` to exactly that sentence.
 *
 * NOT RENDERED: the threshold card's last drawn line, "About 6 in 100 of your
 * leads landed here last month." "Here" is the review queue Epic 22 builds, so
 * there is nothing to count yet, and a number no data backs is the one thing
 * this card must not show (Q-104). It is quoted below so the test can prove the
 * omission is a decision rather than an oversight.
 *
 * NO RUNTIME IMPORTS, so a node-side test can load this file directly.
 */

export const DRAWN = {
  thresholdTitle: 'When to ask a person',
  thresholdAside: 'Confidence threshold',
  percent: '%',
  icpTitle: 'Who you are actually looking for',
  icpLead:
    'Describe your ideal customer in your own words. The model uses this as context. It is never scored on its own.',
  /**
   * Story 20.9 — the refusal the `points` capture draws under the emptied card,
   * naming the one criterion that reads the fit. Two readers are COMPOSED, in
   * `derived-copy.ts`.
   */
  icpRefused: (label: string): string => `${label} scores against this description. Remove that criterion first.`,
} as const

/** The drawn accessible names — attributes in the mockup, so the test reads them from the markup. */
export const LABELS = {
  thresholdSlider: 'Confidence threshold',
  thresholdPercent: 'Confidence threshold percent',
  icp: 'Ideal customer profile',
} as const

/**
 * AC 3 — the copy states what happens below the number, not the number alone.
 * The mockup's own sentence, with its two variables: the threshold, and the band
 * this funnel calls confident.
 */
export const consequence = (threshold: number, topBand: string): string =>
  `Below ${threshold}% confidence, a lead goes to your review queue — whatever its score. A confident ${topBand} lead is routed. An unsure one waits for a person.`

/** The drawn counter, "412 / 4,000". */
export const icpCount = (length: number, max: number): string => `${thousands(length)} / ${thousands(max)}`

/** The drawn line this card deliberately does not render (Q-104). */
export const NOT_RENDERED_STATISTIC = 'About 6 in 100 of your leads landed here last month.'

/** 4000 → "4,000", without leaning on the runtime's locale data. */
function thousands(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}
